<template>
  <transition name="ai-access-notice">
    <aside
      v-if="notice.visible"
      class="ai-access-notice"
      role="status"
      aria-live="polite"
      @mouseenter="pauseAiAccessNotice"
      @mouseleave="resumeAiAccessNotice"
    >
      <button
        type="button"
        class="ai-access-notice__close"
        aria-label="Close"
        @click="dismissAiAccessNotice"
      >
        <v-icon small>mdi-close</v-icon>
      </button>

      <p class="ai-access-notice__kicker">LinkBook AI</p>

      <transition name="ai-access-copy" mode="out-in">
        <div v-if="notice.mode === 'byok'" key="byok">
          <h2 class="ai-access-notice__title">Add your OpenRouter key</h2>
          <p class="ai-access-notice__text">
            Your plan uses your own OpenRouter key. It is encrypted, and it is
            not shown again after you save it.
          </p>
          <ol class="ai-access-notice__steps">
            <li v-for="step in steps" :key="step">{{ step }}</li>
          </ol>
        </div>
        <div v-else :key="feature.id">
          <h2 class="ai-access-notice__title">{{ feature.title }}</h2>
          <p class="ai-access-notice__text">{{ feature.text }}</p>
          <p class="ai-access-notice__note">Part of paid plans.</p>
        </div>
      </transition>

      <div class="ai-access-notice__actions">
        <v-btn color="primary" depressed @click="takeAction">
          {{ actionLabel }}
        </v-btn>
        <a
          v-if="notice.mode === 'byok'"
          class="ai-access-notice__link"
          href="https://openrouter.ai/keys"
          target="_blank"
          rel="noopener noreferrer"
        >
          Create a key on OpenRouter
        </a>
      </div>

      <div :key="notice.session" class="ai-access-notice__timer"></div>
    </aside>
  </transition>
</template>

<script>
import {
  BYOK_STEPS,
  SUBSCRIPTION_FEATURES,
  aiAccessNotice,
  dismissAiAccessNotice,
  pauseAiAccessNotice,
  resumeAiAccessNotice
} from '@/composables/useAIAccessNotification'

export default {
  name: 'AiAccessNotice',
  data() {
    return {
      steps: BYOK_STEPS
    }
  },
  computed: {
    notice() {
      return aiAccessNotice
    },
    feature() {
      return (
        SUBSCRIPTION_FEATURES[this.notice.featureIndex] ||
        SUBSCRIPTION_FEATURES[0]
      )
    },
    actionLabel() {
      return this.notice.mode === 'byok' ? 'Take me to the key' : 'See plans'
    }
  },
  methods: {
    dismissAiAccessNotice,
    pauseAiAccessNotice,
    resumeAiAccessNotice,
    takeAction() {
      const mode = this.notice.mode
      dismissAiAccessNotice()

      if (mode === 'byok') {
        if (
          this.$route.name === 'Profile' &&
          this.$route.hash === '#subscription'
        ) {
          window.dispatchEvent(new CustomEvent('linkbook:focus-api-key'))
          return
        }
        this.$router
          .push({ name: 'Profile', hash: '#subscription' })
          .catch(() => {})
        return
      }

      window.location.assign('/subscriptions')
    }
  }
}
</script>

<style scoped>
.ai-access-notice {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 10000;
  width: 360px;
  max-width: calc(100vw - 32px);
  padding: 20px 20px 16px;
  background: #ffffff;
  color: #1f2933;
  border-radius: 16px;
  border: 1px solid rgba(25, 118, 210, 0.18);
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.18);
  overflow: hidden;
}

.ai-access-notice__close {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 32px;
  height: 32px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #607080;
  cursor: pointer;
}

.ai-access-notice__close:hover {
  background: rgba(25, 118, 210, 0.08);
  color: #1f2933;
}

.ai-access-notice__kicker {
  margin: 0 36px 8px 0;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #1976d2;
}

.ai-access-notice__title {
  margin: 0 28px 8px 0;
  font-size: 18px;
  line-height: 1.3;
  font-weight: 600;
}

.ai-access-notice__text,
.ai-access-notice__note {
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: #3e4c59;
}

.ai-access-notice__note {
  margin-top: 10px;
  font-size: 13px;
  color: #7b8794;
}

.ai-access-notice__steps {
  margin: 12px 0 0;
  padding-left: 18px;
  color: #3e4c59;
  font-size: 14px;
  line-height: 1.45;
}

.ai-access-notice__steps li + li {
  margin-top: 4px;
}

.ai-access-notice__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 16px;
}

.ai-access-notice__link {
  font-size: 13px;
  font-weight: 600;
  color: #1976d2;
  text-decoration: none;
}

.ai-access-notice__link:hover {
  text-decoration: underline;
}

.ai-access-notice__timer {
  position: absolute;
  left: 0;
  bottom: 0;
  height: 3px;
  width: 100%;
  transform-origin: left center;
  background: #1976d2;
  animation: ai-notice-timer 18s linear forwards;
}

.ai-access-notice:hover .ai-access-notice__timer {
  animation-play-state: paused;
}

.ai-access-notice-enter-active,
.ai-access-notice-leave-active,
.ai-access-copy-enter-active,
.ai-access-copy-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.ai-access-notice-enter,
.ai-access-notice-leave-to,
.ai-access-copy-enter,
.ai-access-copy-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

@keyframes ai-notice-timer {
  from {
    transform: scaleX(1);
  }
  to {
    transform: scaleX(0);
  }
}

@media (max-width: 600px) {
  .ai-access-notice {
    right: 16px;
    bottom: 16px;
    left: 16px;
    width: auto;
  }
}
</style>
